import { useSetUnsavedWorkComposerMode } from '@/components/ui/session-ask-heidi/use-persisted-work-mode';
import { useOpenEvidencePanel } from '@/hooks/useOpenEvidencePanel';
import { ASK_USER_APPROVAL_KIND, type InboxItem } from '@heidi/agents/contracts';
import {
  activeWorkPanelProjectIdAtom,
  PENDING_INBOX_PARAMS,
  useInboxItems,
  useSessionWorkflowRuns,
  useWorkflows,
  workPanelProjectIdAtomFamily,
} from '@heidi/agents/data';
import { useCdiNudgesDemoRaw } from '@heidi/agents/flags';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

export interface CdiNudgesDemoConfig {
  mode: 'closed' | 'open';
  routineTitle: string;
  /** Also highlight the quoted note phrase inline. Off unless `true`. */
  inline: boolean;
}

export interface CdiNudgeState {
  config: CdiNudgesDemoConfig | null;
  /** Projects of this session's nudges-Routine runs. */
  nudgeProjectIds: ReadonlySet<string>;
  /** A nudge project with a pending `ask_user` question, or null. */
  readyProjectId: string | null;
  /** The pending `ask_user` inbox item behind `readyProjectId`, or null. */
  readyItem: InboxItem | null;
  /** The newest nudge project of this session (to revisit it), or null. */
  latestProjectId: string | null;
  /** A nudges-Routine run is still working (status `running`) with no ready
   *  query. The backend keeps a run `running` while `ask_user` waits, so a
   *  ready query is never "running" here. */
  isRunning: boolean;
}

const DEFAULT_ROUTINE_TITLE = 'Clarifications';
const EMPTY_IDS: ReadonlySet<string> = new Set();

/** `null` (off) for an empty, malformed, or unknown-mode flag value. */
export const parseCdiNudgesDemoConfig = (raw: string): CdiNudgesDemoConfig | null => {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { mode, routineTitle, inline } = parsed as Record<string, unknown>;
    if (mode !== 'closed' && mode !== 'open') return null;
    return {
      mode,
      routineTitle:
        typeof routineTitle === 'string' && routineTitle
          ? routineTitle
          : DEFAULT_ROUTINE_TITLE,
      inline: inline === true,
    };
  } catch {
    return null;
  }
};

/**
 * CDI nudges demo (RCM-1151). "Ready" means a pending `ask_user`
 * (clarification) approval on a nudges-Routine run of this session — a
 * running run, or any other approval kind, never counts.
 */
export const useCdiNudgeState = (sessionId: string | undefined): CdiNudgeState => {
  const raw = useCdiNudgesDemoRaw();
  // Memoized so `config` keeps one identity per flag value for any consumer's deps array.
  const config = useMemo(() => parseCdiNudgesDemoConfig(raw), [raw]);
  const routineTitle = config?.routineTitle;
  const { data: runsData } = useSessionWorkflowRuns(config ? sessionId : undefined);
  // Shares the sidebar's pending-inbox cache entry, so no extra request.
  // Undefined params disable the query, so the flag-off path mounts nothing.
  const { data: inboxData } = useInboxItems(config ? PENDING_INBOX_PARAMS : undefined);

  // Match the Routine by NAME, then its runs by `source_workflow_id`:
  // `WorkflowRun.title` is the chat title, not the Routine name (the Tasks
  // card resolves the name the same way, `SessionRoutineRunsList.tsx:80`).
  const { data: workflowsData } = useWorkflows({}, { enabled: !!config });
  const nudgeWorkflowIds = useMemo(() => {
    if (!routineTitle) return EMPTY_IDS;
    return new Set(
      (workflowsData?.workflows ?? [])
        .filter((workflow) => workflow.name?.trim() === routineTitle)
        .map((workflow) => workflow.workflow_id)
    );
  }, [routineTitle, workflowsData?.workflows]);

  // Runs of the nudges Routine, newest first (ISO timestamps sort as text).
  const nudgeRuns = useMemo(() => {
    if (nudgeWorkflowIds.size === 0) return [];
    return (runsData?.runs ?? [])
      .filter(
        (run) =>
          !!run.project_id &&
          !!run.source_workflow_id &&
          nudgeWorkflowIds.has(run.source_workflow_id)
      )
      .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));
  }, [nudgeWorkflowIds, runsData?.runs]);

  const nudgeProjectIds = useMemo(
    () =>
      nudgeRuns.length === 0
        ? EMPTY_IDS
        : new Set(nudgeRuns.map((run) => run.project_id)),
    [nudgeRuns]
  );

  const readyItem = useMemo(() => {
    if (nudgeProjectIds.size === 0) return null;
    return (
      inboxData?.items.find(
        (candidate) =>
          candidate.approval_kind === ASK_USER_APPROVAL_KIND &&
          !!candidate.render.domain.project_id &&
          nudgeProjectIds.has(candidate.render.domain.project_id)
      ) ?? null
    );
  }, [inboxData?.items, nudgeProjectIds]);
  const readyProjectId = readyItem?.render.domain.project_id ?? null;

  const latestProjectId = nudgeRuns[0]?.project_id ?? null;

  const isRunning = !readyProjectId && nudgeRuns.some((run) => run.status === 'running');

  return {
    config,
    nudgeProjectIds,
    readyProjectId,
    readyItem,
    latestProjectId,
    isRunning,
  };
};

/**
 * Opens a nudge chat in the session's Work panel WRITABLE — the plain
 * `useSelectWorkPanelProject` opens history read-only. `SessionWorkPanel`
 * keeps it writable because the session pane passes `nudgeProjectIds` as
 * `inScopeProjectIds`. Also forces Work mode: an Evidence-only chat open in the
 * panel would otherwise stay in control and hide the clarification. The switch
 * is not saved, so a saved Evidence-only default survives it.
 */
export const useOpenCdiNudgeChat = (sessionId: string) => {
  const setActive = useSetAtom(activeWorkPanelProjectIdAtom);
  const setRecent = useSetAtom(workPanelProjectIdAtomFamily(sessionId));
  const openPanel = useOpenEvidencePanel();
  const setComposerMode = useSetUnsavedWorkComposerMode();
  return useCallback(
    (projectId: string) => {
      setRecent(projectId);
      setActive({ sessionId, projectId, inspectReadOnly: false });
      setComposerMode('work');
      openPanel();
    },
    [openPanel, sessionId, setActive, setComposerMode, setRecent]
  );
};
